import { useState, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, TransformControls, Html } from '@react-three/drei';
import * as THREE from 'three';

const Room = ({ width, height, depth, wallColors, selectedWall, setSelectedWall }) => {
  const wallThickness = 0.2;
  const floorThickness = 0.1;

  return (
    <group>
      {/* Piso */}
      <mesh position={[0, -height/2, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <boxGeometry args={[width, depth, floorThickness]} />
        <meshStandardMaterial color="#cccccc" />
      </mesh>

      {/* Paredes */}
      {['front', 'back', 'left', 'right'].map((wall) => {
        let position, rotation, dimensions;
        switch(wall) {
          case 'front':
            position = [0, 0, -depth/2];
            dimensions = [width, height, wallThickness];
            break;
          case 'back':
            position = [0, 0, depth/2];
            dimensions = [width, height, wallThickness];
            break;
          case 'left':
            position = [-width/2, 0, 0];
            rotation = [0, Math.PI/2, 0];
            dimensions = [depth, height, wallThickness];
            break;
          case 'right':
            position = [width/2, 0, 0];
            rotation = [0, Math.PI/2, 0];
            dimensions = [depth, height, wallThickness];
            break;
        }

        return (
          <mesh
            key={wall}
            position={position}
            rotation={rotation}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedWall(wall);
            }}
            onPointerOver={() => document.body.style.cursor = 'pointer'}
            onPointerOut={() => document.body.style.cursor = 'auto'}
          >
            <boxGeometry args={dimensions} />
            <meshStandardMaterial 
              color={wallColors[wall]} 
              emissive={selectedWall === wall ? '#555555' : '#000000'}
              emissiveIntensity={selectedWall === wall ? 0.5 : 0}
            />
            {selectedWall === wall && (
              <Html distanceFactor={10} position={[0, height/2 + 0.5, 0]}>
                <div className="bg-white p-1 rounded text-xs shadow-lg">
                  {`Pared ${wall === 'front' ? 'Frontal' : wall === 'back' ? 'Trasera' : wall === 'left' ? 'Izquierda' : 'Derecha'} (Seleccionada)`}
                </div>
              </Html>
            )}
          </mesh>
        );
      })}
    </group>
  );
};

const Shape = ({ 
  id,
  type, 
  position, 
  color, 
  dimensions,
  selected,
  onSelect,
  setTransformActive
}) => {
  const meshRef = useRef();
  const transformRef = useRef();
  const [hovered, setHover] = useState(false);

  useEffect(() => {
    if (!selected || !transformRef.current || !meshRef.current) {
      setTransformActive(false);
      return;
    }

    transformRef.current.attach(meshRef.current);
    setTransformActive(true);

    const controls = transformRef.current;
    const onMouseDown = () => setTransformActive(true);
    const onMouseUp = () => setTransformActive(false);

    controls.addEventListener('mouseDown', onMouseDown);
    controls.addEventListener('mouseUp', onMouseUp);

    return () => {
      controls.removeEventListener('mouseDown', onMouseDown);
      controls.removeEventListener('mouseUp', onMouseUp);
    };
  }, [selected, setTransformActive]);

  const getGeometry = () => {
    switch (type) {
      case 'box':
        return <boxGeometry args={[dimensions.width, dimensions.height, dimensions.depth]} />;
      case 'sphere':
        return <sphereGeometry args={[dimensions.width/2, 32, 32]} />;
      case 'cylinder':
        return <cylinderGeometry args={[dimensions.width/2, dimensions.width/2, dimensions.height, 32]} />;
      default:
        return <boxGeometry args={[1, 1, 1]} />;
    }
  };

  return (
    <group>
      {selected && (
        <TransformControls
          ref={transformRef}
          mode="translate"
          showX={true}
          showY={true}
          showZ={true}
        />
      )}
      <mesh
        ref={meshRef}
        position={position}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHover(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHover(false);
          document.body.style.cursor = 'auto';
        }}
        castShadow
      >
        {getGeometry()}
        <meshStandardMaterial 
          color={hovered ? '#ff9900' : selected ? '#ff0000' : color} 
          transparent={selected || hovered}
          opacity={selected ? 0.8 : hovered ? 0.9 : 1}
        />
        {selected && (
          <Html distanceFactor={10} position={[0, dimensions.height/2 + 0.2, 0]}>
            <div className="bg-white p-1 rounded text-xs shadow-lg">
              {`${type} (${dimensions.width.toFixed(1)}x${dimensions.height.toFixed(1)}x${dimensions.depth.toFixed(1)})`}
            </div>
          </Html>
        )}
      </mesh>
    </group>
  );
};

const KubiRoom = () => {
  const [roomDimensions, setRoomDimensions] = useState({
    width: 5,
    height: 3,
    depth: 4
  });

  const [wallColors, setWallColors] = useState({
    front: '#eeeeee',
    back: '#eeeeee',
    left: '#eeeeee',
    right: '#eeeeee'
  });

  const [selectedWall, setSelectedWall] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [selectedShape, setSelectedShape] = useState(null);
  const [transformActive, setTransformActive] = useState(false);
  const [newShape, setNewShape] = useState({
    type: 'box',
    color: '#3498db',
    dimensions: { width: 0.5, height: 0.5, depth: 0.5 }
  });

  const orbitControlsRef = useRef();

  const addShape = () => {
    const newShapeObj = {
      id: Math.random().toString(36).substring(7),
      type: newShape.type,
      position: [0, 0, 0],
      color: newShape.color,
      dimensions: { ...newShape.dimensions }
    };
    setShapes([...shapes, newShapeObj]);
    setSelectedShape(newShapeObj.id);
  };

  const deleteSelectedShape = () => {
    if (selectedShape) {
      setShapes(shapes.filter(shape => shape.id !== selectedShape));
      setSelectedShape(null);
    }
  };

  const changeWallColor = (color) => {
    if (selectedWall) {
      setWallColors({
        ...wallColors,
        [selectedWall]: color
      });
    }
  };

  useEffect(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = !transformActive;
    }
  }, [transformActive]);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Panel izquierdo - Menú de figuras */}
      <div className="w-64 bg-white p-4 shadow-md overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Añadir Figuras</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Tipo de Figura</label>
          <select 
            className="w-full p-2 border rounded"
            value={newShape.type}
            onChange={(e) => setNewShape({...newShape, type: e.target.value})}
          >
            <option value="box">Caja</option>
            <option value="sphere">Esfera</option>
            <option value="cylinder">Cilindro</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Color</label>
          <input 
            type="color" 
            className="w-full h-10"
            value={newShape.color}
            onChange={(e) => setNewShape({...newShape, color: e.target.value})}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Dimensiones (metros)</label>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs">Ancho</label>
              <input 
                type="number" 
                step="0.1"
                className="w-full p-1 border rounded"
                value={newShape.dimensions.width}
                onChange={(e) => setNewShape({
                  ...newShape, 
                  dimensions: {
                    ...newShape.dimensions,
                    width: parseFloat(e.target.value) || 0.1
                  }
                })}
              />
            </div>
            <div>
              <label className="text-xs">Alto</label>
              <input 
                type="number" 
                step="0.1"
                className="w-full p-1 border rounded"
                value={newShape.dimensions.height}
                onChange={(e) => setNewShape({
                  ...newShape, 
                  dimensions: {
                    ...newShape.dimensions,
                    height: parseFloat(e.target.value) || 0.1
                  }
                })}
              />
            </div>
            <div>
              <label className="text-xs">Profundidad</label>
              <input 
                type="number" 
                step="0.1"
                className="w-full p-1 border rounded"
                value={newShape.dimensions.depth}
                onChange={(e) => setNewShape({
                  ...newShape, 
                  dimensions: {
                    ...newShape.dimensions,
                    depth: parseFloat(e.target.value) || 0.1
                  }
                })}
              />
            </div>
          </div>
        </div>

        <button 
          className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600"
          onClick={addShape}
        >
          Añadir Figura
        </button>

        {selectedShape && (
          <button 
            className="w-full bg-red-500 text-white py-2 rounded hover:bg-red-600 mt-2"
            onClick={deleteSelectedShape}
          >
            Eliminar Seleccionada
          </button>
        )}
      </div>

      {/* Vista 3D principal */}
      <div className="flex-1 relative">
        <Canvas shadows camera={{ position: [10, 5, 10], fov: 25 }}>
          <ambientLight intensity={0.5} />
          <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
          
          <group>
            <Room 
              width={roomDimensions.width} 
              height={roomDimensions.height} 
              depth={roomDimensions.depth}
              wallColors={wallColors}
              selectedWall={selectedWall}
              setSelectedWall={setSelectedWall}
            />
            
            {shapes.map((shape) => (
              <Shape
                key={shape.id}
                id={shape.id}
                type={shape.type}
                position={shape.position}
                color={shape.color}
                dimensions={shape.dimensions}
                selected={selectedShape === shape.id}
                onSelect={setSelectedShape}
                setTransformActive={setTransformActive}
              />
            ))}
          </group>
          
          <OrbitControls 
            ref={orbitControlsRef}
            makeDefault 
            enabled={!transformActive}
          />
          <gridHelper args={[20, 20]} />
        </Canvas>
      </div>

      {/* Panel derecho - Configuración de habitación */}
      <div className="w-64 bg-white p-4 shadow-md overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Configuración de Habitación</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Dimensiones (metros)</label>
          
          <div className="mb-2">
            <label className="text-xs">Ancho</label>
            <input 
              type="range" 
              min="2" 
              max="10" 
              step="0.1"
              className="w-full"
              value={roomDimensions.width}
              onChange={(e) => setRoomDimensions({
                ...roomDimensions,
                width: parseFloat(e.target.value)
              })}
            />
            <span>{roomDimensions.width.toFixed(1)} m</span>
          </div>
          
          <div className="mb-2">
            <label className="text-xs">Alto</label>
            <input 
              type="range" 
              min="2" 
              max="5" 
              step="0.1"
              className="w-full"
              value={roomDimensions.height}
              onChange={(e) => setRoomDimensions({
                ...roomDimensions,
                height: parseFloat(e.target.value)
              })}
            />
            <span>{roomDimensions.height.toFixed(1)} m</span>
          </div>
          
          <div className="mb-2">
            <label className="text-xs">Profundidad</label>
            <input 
              type="range" 
              min="2" 
              max="10" 
              step="0.1"
              className="w-full"
              value={roomDimensions.depth}
              onChange={(e) => setRoomDimensions({
                ...roomDimensions,
                depth: parseFloat(e.target.value)
              })}
            />
            <span>{roomDimensions.depth.toFixed(1)} m</span>
          </div>
        </div>

        {selectedWall && (
          <div className="mb-4">
            <h3 className="font-medium mb-2">Pared Seleccionada: {selectedWall}</h3>
            <input 
              type="color" 
              className="w-full h-10"
              value={wallColors[selectedWall]}
              onChange={(e) => changeWallColor(e.target.value)}
            />
          </div>
        )}

        <h3 className="font-medium mb-2">Figuras Añadidas</h3>
        <div className="space-y-2">
          {shapes.map(shape => (
            <div 
              key={shape.id} 
              className={`p-2 rounded cursor-pointer ${selectedShape === shape.id ? 'bg-blue-100' : 'bg-gray-100'}`}
              onClick={() => setSelectedShape(shape.id)}
            >
              {`${shape.type} (${shape.dimensions.width.toFixed(1)}x${shape.dimensions.height.toFixed(1)}x${shape.dimensions.depth.toFixed(1)})`}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KubiRoom;